// src/pages/Shop.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "../components/ui/Badge";
import Container from "../components/ui/Container";
import TrustBadges from "../components/TrustBadges";
import Breadcrumbs from "../components/Breadcrumbs";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

// Heuristische Kategorien (optional: aus API liefern)
const CATEGORY_BY_SLUG = (slug = "") => {
    if (/beton|rohr|falle/.test(slug)) return "Betonrohrfallen";
    if (/abfang|kasten/.test(slug)) return "Abfangkästen";
    if (/melder|catchalive/.test(slug)) return "Melder & Monitoring";
    if (/schub|stange/.test(slug)) return "Zubehör";
    if (/köder|koeder/.test(slug)) return "Köder";
    if (/pass|pa[eä]sse|p[aä]sse|fallenpa/.test(slug)) return "Dokumentation";
    return "Sonstiges";
};

const CATEGORY_ICONS = {
    "Betonrohrfallen": "🪨",
    "Abfangkästen": "📦",
    "Melder & Monitoring": "📡",
    "Zubehör": "🔧",
    "Köder": "🎣",
    "Dokumentation": "📋",
    "Sonstiges": "📎",
};

export default function Shop() {
    const [all, setAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Filter / UI
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("neu");
    const [activeCat, setActiveCat] = useState(null); // single category or null for all

    useEffect(() => {
        document.title = "Betonrohrfalle & Jagdzubehör kaufen | Shop | Tuscher Helmut";
        
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Betonrohrfalle kaufen ✓ Abfangkasten ✓ Fallenmelder ✓ Schubstange | Hochwertige Jagdausrüstung für die Raubwildbejagung. 40 Jahre Erfahrung. Jetzt bestellen!");
        }
        
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = 'https://tuscherhelmut-betonrohrfallen.de/shop';
    }, []);

    useEffect(() => {
        const url = `${API_BASE}/api/products.php`;
        setLoading(true);
        setErr("");
        fetch(url, { credentials: "omit" })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                if (!Array.isArray(data)) throw new Error("Unerwartetes Antwortformat");
                const normalized = data.map((p) => {
                    const price_cents = Number(p.price_cents ?? 0);
                    const image = (p.image || "").replace(/^\/+/, "");
                    const title = p.title || p.slug;
                    const description = p.description || "";
                    const category = CATEGORY_BY_SLUG(p.slug || "");
                    const sale_price = Number(p.sale_price ?? 0);
                    const sale_price_cents = sale_price > 0 ? Math.round(sale_price * 100) : null;
                    const effective_price_cents = sale_price_cents ?? price_cents;
                    return {
                        ...p,
                        price_cents,
                        effective_price_cents,
                        image,
                        title,
                        description,
                        category,
                        sale_price: sale_price_cents ? sale_price_cents / 100 : null,
                        requires_freight: !!p.requires_freight,
                        free_shipping: !!p.free_shipping,
                    };
                });
                setAll(normalized);
            })
            .catch((e) => setErr(`Konnte Produkte nicht laden (${e.message}).`))
            .finally(() => setLoading(false));
    }, []);

    // Kategorien + Zähler
    const catList = useMemo(() => {
        const m = new Map();
        all.forEach((p) => m.set(p.category, (m.get(p.category) || 0) + 1));
        return Array.from(m.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, count]) => ({ label, count }));
    }, [all]);

    // Gefilterte + sortierte Liste
    const products = useMemo(() => {
        let items = all.slice();

        // Textsuche
        if (q.trim()) {
            const s = q.toLowerCase();
            items = items.filter(
                (p) =>
                    (p.title || "").toLowerCase().includes(s) ||
                    (p.description || "").toLowerCase().includes(s) ||
                    (p.slug || "").toLowerCase().includes(s)
            );
        }

        // Kategorie
        if (activeCat) {
            items = items.filter((p) => p.category === activeCat);
        }

        // Sortierung
        switch (sort) {
            case "az":
                items.sort((a, b) => a.title.localeCompare(b.title));
                break;
            case "za":
                items.sort((a, b) => b.title.localeCompare(a.title));
                break;
            case "preis-auf":
                items.sort(
                    (a, b) =>
                        (a.effective_price_cents ?? a.price_cents) -
                        (b.effective_price_cents ?? b.price_cents)
                );
                break;
            case "preis-ab":
                items.sort(
                    (a, b) =>
                        (b.effective_price_cents ?? b.price_cents) -
                        (a.effective_price_cents ?? a.price_cents)
                );
                break;
            default:
                break;
        }

        return items;
    }, [all, q, activeCat, sort]);

    return (
        <section className="relative overflow-hidden min-h-screen">
            {/* Hintergrund Dekoration */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-[10%] -left-[10%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px] dark:bg-brand-500/10" />
                <div className="absolute top-[20%] -right-[5%] h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px] dark:bg-blue-500/5" />
                <div className="absolute -bottom-[10%] left-[20%] h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px] dark:bg-brand-500/10" />
                
                <div 
                    className="absolute inset-0 opacity-[0.15] dark:opacity-[0.05]" 
                    style={{ 
                        backgroundImage: 'radial-gradient(#64748b 0.5px, transparent 0.5px)', 
                        backgroundSize: '24px 24px' 
                    }} 
                />
            </div>

            <Container className="relative py-14 sm:py-20 lg:py-24">
                {/* Breadcrumbs */}
                <Breadcrumbs 
                    items={[{ label: "Shop" }]}
                    className="mb-6"
                />

                {/* Trust Badges */}
                <TrustBadges layout="horizontal" className="mb-10 pb-6 border-b border-slate-200/60 dark:border-slate-800" />

                {/* Hero Header */}
                <div className="text-center mb-10">
                    <Badge className="mb-4 bg-brand-50 text-brand-600 dark:bg-brand-900/20 border-brand-100/50">Meine Auswahl</Badge>
                    <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight text-slate-900 dark:text-white">Shop</h1>
                    <p className="mt-3 text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                        Entdecke meine praxiserprobten Betonrohrfallen, zuverlässige Melder und professionelles Zubehör.
                    </p>
                </div>

                {/* Horizontale Filterleiste */}
                <div className="mb-8 rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm p-4 shadow-sm">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Kategorie-Chips */}
                        <div className="flex-1 flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => setActiveCat(null)}
                                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                    !activeCat
                                        ? "bg-brand-600 text-white shadow-md shadow-brand-600/25"
                                        : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                }`}
                            >
                                Alle
                                <span className={`text-xs ${!activeCat ? "text-brand-200" : "text-slate-400"}`}>
                                    ({all.length})
                                </span>
                            </button>
                            {catList.map((c) => (
                                <button
                                    key={c.label}
                                    onClick={() => setActiveCat(activeCat === c.label ? null : c.label)}
                                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                        activeCat === c.label
                                            ? "bg-brand-600 text-white shadow-md shadow-brand-600/25"
                                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                    }`}
                                >
                                    <span>{CATEGORY_ICONS[c.label] || "📎"}</span>
                                    {c.label}
                                    <span className={`text-xs ${activeCat === c.label ? "text-brand-200" : "text-slate-400"}`}>
                                        ({c.count})
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Suche + Sortierung */}
                        <div className="flex items-center gap-2 shrink-0">
                            <div className="relative">
                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Suche…"
                                    className="w-44 sm:w-52 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-all"
                                />
                            </div>
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-all"
                            >
                                <option value="neu">Neu</option>
                                <option value="az">A → Z</option>
                                <option value="za">Z → A</option>
                                <option value="preis-auf">Preis ↑</option>
                                <option value="preis-ab">Preis ↓</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Ergebnis-Info */}
                {!loading && !err && (
                    <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                        {products.length} {products.length === 1 ? "Produkt" : "Produkte"}
                        {activeCat && <> in <strong className="text-slate-700 dark:text-slate-300">{activeCat}</strong></>}
                        {q.trim() && <> für „<strong className="text-slate-700 dark:text-slate-300">{q}</strong>"</>}
                    </p>
                )}

                {/* Status */}
                {loading && <p className="text-slate-600 dark:text-slate-300">Lade Produkte …</p>}
                {err && <p className="text-red-600 dark:text-red-400">{err}</p>}
                {!loading && !err && products.length === 0 && (
                    <div className="text-center py-16">
                        <div className="text-5xl mb-4">🔍</div>
                        <p className="text-lg font-medium text-slate-600 dark:text-slate-300">Keine Produkte gefunden</p>
                        <p className="text-sm text-slate-500 mt-1">Versuche eine andere Suche oder setze die Filter zurück.</p>
                        <button
                            onClick={() => { setQ(""); setActiveCat(null); }}
                            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-500 transition-colors"
                        >
                            Filter zurücksetzen
                        </button>
                    </div>
                )}

                {/* Produkt-Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                    {!loading &&
                        !err &&
                        products.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/produkt/${p.slug}`}
                                className="group relative flex flex-col rounded-3xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-brand-500/10 hover:-translate-y-2 hover:border-brand-200 dark:hover:border-brand-800"
                            >
                                {/* Bild-Bereich */}
                                <div className="relative h-64 w-full overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 flex items-center justify-center">
                                    {p.image ? (
                                        <img
                                            src={`/${p.image}`}
                                            alt={p.title}
                                            className="max-h-56 w-auto object-contain transition-all duration-700 group-hover:scale-110"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="text-slate-400 text-sm italic">Kein Bild</div>
                                    )}
                                    
                                    {/* Badges Overlay */}
                                    <div className="absolute left-3 top-3 flex flex-col gap-2">
                                        {p.free_shipping && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white/90 dark:bg-slate-900/90 px-3 py-1 text-xs font-bold text-brand-600 shadow-sm ring-1 ring-brand-100">
                                                ✓ Gratis Versand
                                            </span>
                                        )}
                                    </div>

                                    {/* Sale Badge */}
                                    {p.sale_price && (
                                        <div className="absolute right-3 top-3">
                                            <div className="rounded-full bg-red-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-white shadow-lg animate-pulse">
                                                Angebot
                                            </div>
                                        </div>
                                    )}

                                    {/* Hover Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-900/80 via-brand-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center pb-6">
                                        <span className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-brand-700 shadow-xl translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                            Zum Produkt
                                            <svg className="h-4 w-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="flex flex-1 flex-col p-5">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                                            <span>{CATEGORY_ICONS[p.category] || "📎"}</span>
                                            {p.category}
                                        </span>
                                        {p.requires_freight && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20 rounded-full px-2 py-0.5">
                                                🚚 Spedition
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 group-hover:text-brand-600 transition-colors duration-300 leading-snug">
                                        {p.title}
                                    </h3>
                                    
                                    {p.description && (
                                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                                            {p.description}
                                        </p>
                                    )}

                                    {/* Preis */}
                                    <div className="mt-auto pt-5 flex items-end justify-between gap-2 border-t border-slate-100 dark:border-slate-800 mt-4">
                                        <div className="flex flex-col">
                                            {p.sale_price ? (
                                                <>
                                                    <span className="text-xs text-slate-400 line-through decoration-red-400/50">
                                                        {(p.price_cents / 100).toFixed(2)} €
                                                    </span>
                                                    <span className="text-2xl font-black text-slate-900 dark:text-slate-100 tabular-nums tracking-tight">
                                                        {Number(p.sale_price).toFixed(2)} €
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-2xl font-black text-slate-900 dark:text-slate-100 tabular-nums tracking-tight">
                                                    {(p.price_cents / 100).toFixed(2)} €
                                                </span>
                                            )}
                                            <span className="text-[9px] text-slate-400 mt-0.5">zzgl. Versand</span>
                                        </div>

                                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-brand-600 group-hover:text-white text-slate-400 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-brand-600/25">
                                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}

                    {/* Skelett-Loader */}
                    {loading &&
                        Array.from({ length: 4 }).map((_, i) => (
                            <div
                                key={i}
                                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden animate-pulse"
                            >
                                <div className="h-64 bg-slate-100 dark:bg-slate-800" />
                                <div className="p-5">
                                    <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
                                    <div className="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
                                    <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 rounded mb-6" />
                                    <div className="h-8 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
                                </div>
                            </div>
                        ))}
                </div>
            </Container>
        </section>
    );
}
